import { useCallback, useEffect, useState } from 'react'

/** L'event non-standard que Chromium émet avant sa mini-infobar d'install. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'corsalia.pwa.install.dismissed.v1'

/** Déjà installée / lancée en app (Chromium + iOS Safari). */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

/** iOS Safari « vrai » (pas Chrome/Firefox/Edge iOS) : pas de beforeinstallprompt. */
export function isIosSafari(): boolean {
  const ua = window.navigator.userAgent
  return /iPad|iPhone|iPod/.test(ua) && /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)
}

export function usePwaInstall() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1'
    } catch {
      return false
    }
  })
  const [standalone] = useState<boolean>(() => isStandalone())
  const [iosSafari] = useState<boolean>(() => isIosSafari())

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault() // empêche la mini-infobar native → on pilote l'invite
      setDeferred(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => setDeferred(null)
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferred) return
    await deferred.prompt()
    await deferred.userChoice
    setDeferred(null) // l'event n'est utilisable qu'une fois
  }, [deferred])

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      /* localStorage unavailable (private mode) — keep the in-memory choice */
    }
    setDismissed(true)
  }, [])

  return {
    canInstall: deferred !== null,
    isStandalone: standalone,
    isIosSafari: iosSafari,
    dismissed,
    promptInstall,
    dismiss,
  }
}
